import axios from "axios";
import { LoginBody, RetrieveNCavitesByUserBody, RetrieveCavitesByQueryBody, CRUDCaviteBody, ChangeCaviteStatusBody, ChangeCaviteStatusByQueryBody, ProjectCoordsBody, GetDistanceBody, CheckBssBody, CheckCoordCommuneBody, CRUDUserBody, SpatialCaviteReqBody, ExportPDFBody, UploadBody, SheetExportBody} from './bodies';
import { parse } from '@/lib/models/abstract-model';
import BeanCaviteAssociee from '@/lib/models/bean-cavite-associee';
import BeanCoord from '@/lib/models/bean-coord';
import BeanLexique from '@/lib/models/bean-lexique';
import BeanQueryResult from "@/lib/models/bean-query-result";
import CavCavite from '@/lib/models/cav-cavite';
import ComDept from "@/lib/models/com-dept";
import User from '@/lib/models/user';
import ComCommune from '../models/com-commune';

const {
  REST_EP_URL
} = (window as any).rootConfig;

const root = axios.create({ baseURL: REST_EP_URL, withCredentials: (window as any).rootConfig.EMBEDDED === true });

const fake = {
  get: (ep) => fetch(`/fake-services/${ep}.json`)
    .then(res => res.json())
    .then(d => ({data: d})),
  post: (ep, body) => fetch(`/fake-services/${ep}.json`)
    .then(res => res.json())
    .then(d => ({data: d}))
};


export const FILE_PROXY_URL = `${REST_EP_URL}io/file-proxy`;

export const logged = async (): Promise<User> => {
  return root.get("logged")
    .then(res => parse(res.data));
};

export const logout = async (): Promise<User> => {
  return root.get("logout")
    .then(res => parse(res.data));
};

export const login = async (body: LoginBody): Promise<User> => {
  return root.post("login", body)
    .then(res => {
      if (res.data) {
        return parse(res.data)
      } else {
        throw new Error("login:failed");
      }
    });
};

export const retrieveNCavitesByUser = async (body: RetrieveNCavitesByUserBody): Promise<CavCavite[]> => {
  return root.post("retrieve-n-cavites-by-user", body)
    .then(res => parse(res.data));
};

export const retrieveCavitesByQuery = async (body: RetrieveCavitesByQueryBody): Promise<BeanQueryResult> => {
  return root.post("retrieve-cavites-by-query", body)
    .then(res => parse(res.data));
};

export const getCaviteByNum = async (numCavite: string): Promise<CavCavite> => {
  return root.get("get-cavite-by-num", {params: {numCavite}})
    .then(res => parse(res.data));
};

export const saveCavite = async (body: CRUDCaviteBody): Promise<CavCavite> => {
  return root.post("save-cavite", body)
    .then(res => parse(res.data));
};

export const deleteCavite = async (body: Pick<CavCavite, "numCavite">): Promise<boolean> => {
  return root.post("delete-cavite", body)
    .then(res => res.data);
};

export const getCavitesAssociees = async (numCavites: string): Promise<BeanCaviteAssociee[]> => {
  return root.get("get-cavites-associees", {params: {numCavites}})
    .then(res => parse(res.data));
};

export const changeCaviteStatusByQuery = async (body: ChangeCaviteStatusByQueryBody): Promise<boolean> => {
  return root.post("change-cavite-status-by-query", body)
    .then(res => res.data);
};

export const projectCoords = async (body: ProjectCoordsBody): Promise<BeanCoord> => {
  return root.post("project-coords", body)
    .then(res => parse(res.data));
};

export const getDistance = async (body: GetDistanceBody): Promise<number> => {
  return root.post("get-distance", body)
    .then(res => res.data);
};

export const checkBss = async (body: CheckBssBody): Promise<BeanCoord> => {
  return root.post("check-bss", body)
    .then(res => parse(res.data));
};

export const checkCoordCommune = async (body: CheckCoordCommuneBody): Promise<boolean> => {
  return root.post("check-coord-commune", body)
    .then(res => res.data);
};

export const getLexiques = async (): Promise<BeanLexique> => {
  return root.get("get-lexiques")
    .then(res => parse(res.data));
};

export const getCommunes = async (numDept?: string, filterText?: string): Promise<ComCommune[]> => {
  return root.get("get-communes", {params: {numDept, filterText}})
    .then(res => parse(res.data));
};

export const getDepts = async (codeRegion: string): Promise<ComDept[]> => {
  return root.get("get-depts", {params: {codeRegion}})
    .then(res => parse(res.data));
};

export const saveUser = async (body: CRUDUserBody) => {
  return root.post("save-user", body)
    .then(res => res.data);
};

export const getAllUsers = async () => {
  return root.get("get-all-users")
    .then(res => parse(res.data));
};

export const loadCavitesByCenter = async (body: SpatialCaviteReqBody): Promise<BeanCoord[]> => {
  return root.post("load-cavites-by-center", body)
    .then(res => parse(res.data.list));
};

export const loadMouvementsByCenter = async (body: SpatialCaviteReqBody): Promise<BeanCoord[]> => {
  return root.post("load-mouvements-by-center", body)
    .then(res => parse(res.data.list));
};

export const checkDistanceWithOtherCavites = async (body: SpatialCaviteReqBody): Promise<BeanQueryResult<BeanCoord>> => {
  return root.post("check-distance-with-other-cavites", body)
    .then(res => res.data);
};

export const checkIdBdMvt = async (idBdMvt: number): Promise<boolean> => {
  return root.get("check-id-bd-mvt", {params: {idBdMvt}})
    .then(res => res.data);
};

export const exportPdf = async (body: ExportPDFBody): Promise<string> => {
  return root.post("export-pdf", body)
    .then(res => res.data);
};

export const uploadDoc = async (body: UploadBody): Promise<{ mimeType:string; file:string; originalFile:string }> => {
  const data = body.data;
  const config = typeof body.onUploadProgress === 'function' ? { onUploadProgress: body.onUploadProgress } : {};
  return root.post("/io/upload-doc", data, config).then(res => res.data );
};

export const uploadShp = async (body: UploadBody, isCus: boolean = false): Promise<{ mimeType:string; file:string; originalFile:string }> => {
  const data = body.data;
  const config = typeof body.onUploadProgress === 'function' ? { onUploadProgress: body.onUploadProgress } : {};
  return root.post(`/io/upload-shp?cus=${isCus ? "true": "false"}`, data, config).then(res => res.data );
};

export const sheetExport = async (body: SheetExportBody): Promise<void> => {
  window.open(root.defaults.baseURL + root.getUri({url: "export/sheet", params: {b: body}}), "_blank");
};